/** Onde pôr o balão do guia para ele caber no ecrã.
 *
 *  À primeira limitei só a horizontal e deixei a vertical à solta. O
 *  passo que aponta para a régua da esquerda, que ocupa a altura toda,
 *  ficou com o balão 153 px abaixo do ecrã: metade das frases
 *  invisíveis. Foi o Rui que deu por isso, não eu, porque o meu
 *  ambiente não desenha a aplicação.
 *
 *  Por isso isto é agora uma função pura, com testes: é a única parte
 *  do guia onde um erro se vê a olho e não aparece em teste nenhum de
 *  comportamento.
 */

export interface Rect {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

export interface Janela {
  largura: number
  altura: number
}

export interface Posicao {
  esquerda: number
  topo: number
}

/** Folga entre o balão e o elemento que ele aponta, e entre o balão e
 *  os bordos do ecrã. */
export const FOLGA = 12
export const MARGEM = 8

function limitar(valor: number, min: number, max: number): number {
  return Math.min(Math.max(valor, min), max)
}

/**
 * Escolhe a posição do balão.
 *
 * A ordem por que se tenta:
 *
 *  1. o lado pedido (por baixo ou por cima do elemento);
 *  2. o lado contrário, se o pedido não couber;
 *  3. ao lado, à direita do elemento, que é o que resolve os elementos
 *     altos como a régua, onde nem por cima nem por baixo há espaço;
 *  4. à esquerda, se à direita também não couber;
 *  5. e, em último caso, encostado ao ecrã.
 *
 * O balão **nunca** sai do ecrã: o último passo garante-o sempre.
 */
export function posicaoDoBalao(
  alvo: Rect,
  balao: { width: number; height: number },
  janela: Janela,
  lado: 'cima' | 'baixo',
): Posicao {
  const maxEsq = Math.max(MARGEM, janela.largura - balao.width - MARGEM)
  const maxTopo = Math.max(MARGEM, janela.altura - balao.height - MARGEM)

  /** A última palavra: o balão nunca sai do ecrã, venha o candidato de
   *  onde vier. Um teste com o alvo acima do ecrã devolvia um topo
   *  negativo porque eu só verificava o bordo de baixo; com isto,
   *  deixa de haver caminho por onde isso possa acontecer. */
  const dentro = (esquerda: number, topo: number): Posicao => ({
    esquerda: limitar(esquerda, MARGEM, maxEsq),
    topo: limitar(topo, MARGEM, maxTopo),
  })

  // Centrado no elemento, na horizontal, para os casos de cima/baixo.
  const centrado = alvo.left + alvo.width / 2 - balao.width / 2

  const porBaixo = alvo.bottom + FOLGA
  const porCima = alvo.top - balao.height - FOLGA
  // Os DOIS bordos, não só um: um elemento acima do ecrã "cabia por
  // baixo" pelo bordo de baixo e punha o balão em cima do nada.
  const cabePorBaixo =
    porBaixo >= MARGEM && porBaixo + balao.height + MARGEM <= janela.altura
  const cabePorCima =
    porCima >= MARGEM && porCima + balao.height + MARGEM <= janela.altura

  if (lado === 'baixo' && cabePorBaixo) return dentro(centrado, porBaixo)
  if (lado === 'cima' && cabePorCima) return dentro(centrado, porCima)
  // O lado pedido não coube: tenta o contrário.
  if (cabePorBaixo) return dentro(centrado, porBaixo)
  if (cabePorCima) return dentro(centrado, porCima)

  // Nem por cima nem por baixo: é um elemento alto, como a régua da
  // esquerda. Põe-se ao lado, alinhado pelo topo do elemento.
  const aDireita = alvo.right + FOLGA
  if (
    aDireita >= MARGEM &&
    aDireita + balao.width + MARGEM <= janela.largura
  ) {
    return dentro(aDireita, alvo.top)
  }
  const aEsquerda = alvo.left - balao.width - FOLGA
  if (aEsquerda >= MARGEM) return dentro(aEsquerda, alvo.top)

  // Não há sítio bom: encosta-se ao ecrã, mas dentro dele.
  return dentro(centrado, porBaixo)
}
